import { api } from './api';
import type { OfficialDocumentStatus } from '../utils/workflowConstants';
import { WorkflowActionCodes, WorkflowStepType, normalizeDocumentStatus } from '../utils/workflowConstants';

export type { OfficialDocumentStatus };
export { WorkflowActionCodes, WorkflowStepType, normalizeDocumentStatus };

export interface WorkflowStepAssignee {
  id: string;
  workflowStepId: string;
  userId?: string;
  teamId?: string;
}

export interface WorkflowStep {
  id: string;
  workflowId: string;
  name: string;
  description: string;
  type: string;
  order: number;
  slaId?: string;
  sendEmailNotification: boolean;
  requiresComment: boolean;
  approvalRule: 'Any' | 'All';
  minApprovals: number;
  allowedActions: string[];
  assignees: WorkflowStepAssignee[];
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  /** Ausente ou null em workflows de sistema globais (todos os tenants). */
  customerId?: string | null;
  isSystem: boolean;
  isDefault: boolean;
  isActive: boolean;
  steps: WorkflowStep[];
}

export type WorkflowCreatePayload = Omit<Workflow, 'id'>;
export type WorkflowUpdatePayload = Omit<Workflow, 'id'>;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function isRfc4122GuidString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim())
  );
}

/** IDs temporários do UI (ex.: `new-…`) não desserializam em `Guid` no ASP.NET — normaliza antes do POST/PUT. */
function guidOrEmpty(value: string | undefined | null): string {
  return isRfc4122GuidString(value) ? value.trim() : EMPTY_GUID;
}

function optionalGuidString(value: string | undefined | null): string | undefined {
  if (value == null || value === '') return undefined;
  return isRfc4122GuidString(value) ? value.trim() : undefined;
}

function normalizeStepForApi(step: WorkflowStep, parentWorkflowId?: string): WorkflowStep {
  const assignees = (step.assignees ?? []).map(a => ({
    ...a,
    id: guidOrEmpty(a.id),
    workflowStepId: guidOrEmpty(a.workflowStepId),
    userId: optionalGuidString(a.userId),
    teamId: optionalGuidString(a.teamId),
  }));

  // Preferência: id do workflow pai (em update, vem da URL).
  // Fallback: workflowId do próprio step, se for GUID válido.
  // Caso contrário: EMPTY_GUID (no create o handler do backend sobrescreve com o novo Guid).
  const workflowId = isRfc4122GuidString(parentWorkflowId)
    ? parentWorkflowId!.trim()
    : isRfc4122GuidString(step.workflowId)
      ? step.workflowId.trim()
      : EMPTY_GUID;

  return {
    ...step,
    id: guidOrEmpty(step.id),
    workflowId,
    description: step.description ?? '',
    slaId: optionalGuidString(step.slaId),
    assignees,
  };
}

function normalizeWorkflowBodyForApi(
  w: Omit<Workflow, 'id'>,
  parentWorkflowId?: string
): Omit<Workflow, 'id'> {
  // `Guid?` no backend — se não houver GUID válido, envia null (nunca string vazia).
  const customerId = optionalGuidString(w.customerId ?? undefined) ?? null;

  return {
    ...w,
    customerId,
    steps: (w.steps ?? []).map(s => normalizeStepForApi(s, parentWorkflowId)),
  };
}

class WorkflowService {
  async getWorkflows(): Promise<Workflow[]> {
    try {
      const response = await api.get<Workflow[]>('/workflows');
      return response.data ?? [];
    } catch (error: any) {
      throw new Error('Erro ao buscar workflows: ' + error.message);
    }
  }

  async getWorkflowsByCustomer(customerId: string): Promise<Workflow[]> {
    try {
      const response = await api.get<Workflow[]>(`/workflows/customers/${customerId}`);
      return response.data ?? [];
    } catch (error: any) {
      throw new Error('Erro ao buscar workflows do cliente: ' + error.message);
    }
  }

  async getWorkflow(id: string): Promise<Workflow> {
    try {
      const response = await api.get<Workflow>(`/workflows/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Erro ao buscar workflow ${id}: ` + error.message);
    }
  }

  /** Etapas do modelo (`GET /api/workflowsteps?workflowId=`). Útil quando `GET /workflows/{id}` não inclui `steps`. */
  async getWorkflowStepsForWorkflow(workflowId: string): Promise<WorkflowStep[]> {
    try {
      const response = await api.get<WorkflowStep[]>('/workflowsteps', {
        params: { workflowId },
      });
      return response.data ?? [];
    } catch (error: any) {
      throw new Error(`Erro ao buscar etapas do workflow ${workflowId}: ` + error.message);
    }
  }

  async createWorkflow(workflow: WorkflowCreatePayload): Promise<Workflow> {
    try {
      const body = normalizeWorkflowBodyForApi(workflow);
      const response = await api.post<Workflow>('/workflows', body);
      return response.data;
    } catch (error: any) {
      throw new Error('Erro ao criar workflow: ' + error.message);
    }
  }

  async updateWorkflow(id: string, workflow: WorkflowUpdatePayload): Promise<Workflow> {
    try {
      const body = normalizeWorkflowBodyForApi(workflow, id);
      const response = await api.put<Workflow>(`/workflows/${id}`, body);
      return response.data;
    } catch (error: any) {
      throw new Error(`Erro ao atualizar workflow ${id}: ` + error.message);
    }
  }

  /** 204 = imediato; 202 = fila Hangfire (documentos vinculados). */
  async deleteWorkflow(id: string): Promise<
    { mode: 'completed' } | { mode: 'queued'; jobId: string; message: string }
  > {
    try {
      const response = await api.delete<{ jobId: string; message: string }>(`/workflows/${id}`, {
        validateStatus: status => status === 204 || status === 202,
      });
      if (response.status === 202 && response.data?.message) {
        return {
          mode: 'queued',
          jobId: response.data.jobId ?? '',
          message: response.data.message,
        };
      }
      return { mode: 'completed' };
    } catch (error: any) {
      throw new Error(`Erro ao excluir workflow ${id}: ` + error.message);
    }
  }

  /** 204 = imediato; 202 = fila ao existirem instâncias com a etapa. */
  async deleteWorkflowStep(stepId: string): Promise<
    { mode: 'completed' } | { mode: 'queued'; jobId: string; message: string }
  > {
    try {
      const response = await api.delete<{ jobId: string; message: string }>(`/workflowsteps/${stepId}`, {
        validateStatus: status => status === 204 || status === 202,
      });
      if (response.status === 202 && response.data?.message) {
        return {
          mode: 'queued',
          jobId: response.data.jobId ?? '',
          message: response.data.message,
        };
      }
      return { mode: 'completed' };
    } catch (error: any) {
      throw new Error(`Erro ao excluir etapa ${stepId}: ` + error.message);
    }
  }

  async setDefault(id: string): Promise<Workflow> {
    try {
      const workflow = await this.getWorkflow(id);
      return await this.updateWorkflow(id, { ...workflow, isDefault: true });
    } catch (error: any) {
      throw new Error(`Erro ao definir workflow padrão: ` + error.message);
    }
  }

  async updateWorkflowStepsOrder(
    workflowId: string,
    steps: { id: string; order: number }[]
  ): Promise<void> {
    try {
      await api.put('/workflowsteps/reorder', { workflowId, steps });
    } catch (error: any) {
      throw new Error('Erro ao atualizar ordem das etapas: ' + error.message);
    }
  }
}

export const workflowService = new WorkflowService();
